// Centralised group cache-invalidation. Group mutations (join, leave,
// update, archive, role change, broadcast publish) invalidate the same
// constellation of keys — viewer-role, member list, detail caches, the
// "mine" filter view, the broadcast scope under that group. A viewer's own
// membership change (join / leave) additionally invalidates the post feed,
// because membership is what the feed's read gate turns on. Before this
// module, 8+ mutation hooks each spelled out their own invalidate calls;
// a change to "what does a join invalidate?" required touching every site.
//
// Now: every group mutation's `onSettled` calls one of the helpers below.
// The function knows which keys to touch based on the `cause` discriminator,
// and which kind-specific keys to touch based on `kind`.

import type { QueryClient } from '@tanstack/react-query';
import { broadcastKeys } from '../queries/broadcasts';
import { feedKeys } from '../queries/feed';
import { groupKeys } from '../queries/groups';
import { spacesKeys } from '../queries/spaces';
import { invalidateAndRefetchAll } from '../query-cache-utils';

export type GroupKind = 'circle' | 'network' | 'space';

export type GroupMutationCause =
  // viewer joined / left / withdrew — flips viewer-state widely
  | 'join'
  | 'leave'
  // group's own metadata changed (name, image, tone, etc.)
  | 'update'
  // archive / restore: invalidate across all kinds (id-only resolves server-side)
  | 'archive'
  // membership-management: role change, remove member, leadership transfer
  | 'members'
  // join-request approval/decline (leader-side)
  | 'request-decision'
  // broadcast write — invalidates the broadcast scope under this group
  | 'broadcast';

/**
 * Invalidate every cached key that a group mutation could have affected.
 *
 * Call from a mutation's `onSettled` once: it dispatches on `cause` to the
 * right key set. Callers that mutate cross-kind state (e.g. unknown-kind
 * archive) pass the kind they have; the archive case fans out across all
 * three kinds because the server resolves kind from id.
 */
export function invalidateGroupAfterMutation(
  qc: QueryClient,
  kind: GroupKind,
  groupId: string,
  cause: GroupMutationCause,
): void {
  switch (cause) {
    case 'join':
    case 'leave': {
      qc.invalidateQueries({ queryKey: groupKeys.viewerRole(groupId).queryKey });
      invalidateMembersList(qc, groupId);
      invalidateDetailForKind(qc, kind, groupId);
      // "My X" filter view is usually inactive when a join/leave fires
      // (user is browsing all-X). Default invalidate marks it stale but
      // doesn't refetch — the next tab switch would flash stale rows.
      invalidateMineForKind(qc, kind);
      // Membership is the feed's read gate: joining reveals the group's posts,
      // leaving hides them. Refresh the feed the same way a new post does so
      // the joined member sees the historical + pinned posts immediately
      // instead of the stale pre-join feed (issue #162).
      invalidateViewerFeed(qc);
      // Requesting to join adds a row to the directory's "Pending Requests"
      // section; joining an open group / leaving never adds one, so this is a
      // no-op refetch there. Keyed once — the read is kind-agnostic.
      qc.invalidateQueries({ queryKey: groupKeys.myPendingRequests().queryKey });
      return;
    }
    case 'update': {
      invalidateDetailForKind(qc, kind, groupId);
      invalidateListsForKind(qc, kind);
      return;
    }
    case 'archive': {
      // Archive endpoints are kind-agnostic on the server side — we don't
      // always know which kind a group is at hook-call time. Fan out across
      // every kind so whichever cache held the row gets invalidated.
      qc.invalidateQueries({ queryKey: groupKeys.circle.detail(groupId).queryKey });
      qc.invalidateQueries({ queryKey: groupKeys.circle.lists() });
      qc.invalidateQueries({ queryKey: groupKeys.network.detail(groupId).queryKey });
      qc.invalidateQueries({ queryKey: groupKeys.network.lists() });
      qc.invalidateQueries({ queryKey: spacesKeys.detail(groupId).queryKey });
      qc.invalidateQueries({ queryKey: spacesKeys.lists() });
      // `circle.mine` is keyed by { includeArchived, pageSize }; refetch every
      // variant via the opts-less prefix.
      invalidateAndRefetchAll(qc, {
        queryKey: [...groupKeys.circle.all, 'mine'],
      });
      return;
    }
    case 'members': {
      invalidateMembersList(qc, groupId);
      qc.invalidateQueries({ queryKey: groupKeys.viewerRole(groupId).queryKey });
      // We don't know the kind at the role/remove endpoint (it's kind-
      // agnostic on the server) — fan out detail invalidations across
      // every kind so whichever cache holds the row gets refreshed.
      qc.invalidateQueries({ queryKey: groupKeys.circle.detail(groupId).queryKey });
      qc.invalidateQueries({ queryKey: groupKeys.network.detail(groupId).queryKey });
      qc.invalidateQueries({ queryKey: groupKeys.network.leaders(groupId).queryKey });
      qc.invalidateQueries({ queryKey: spacesKeys.detail(groupId).queryKey });
      return;
    }
    case 'request-decision': {
      qc.invalidateQueries({ queryKey: joinRequestsForKind(kind, groupId) });
      invalidateMembersList(qc, groupId);
      invalidateDetailForKind(qc, kind, groupId);
      return;
    }
    case 'broadcast': {
      const scope = {
        kind: 'group' as const,
        groupKind: kind,
        groupId,
      };
      qc.invalidateQueries({
        queryKey: [...broadcastKeys.scope(scope), 'list'] as const,
      });
      qc.invalidateQueries({ queryKey: broadcastKeys.statusCounts(scope).queryKey });
      return;
    }
  }
}

/**
 * Invalidate the viewer's post feed after their own membership changed.
 *
 * A join/leave/invite-accept flips the feed's read gate, so every feed *list*
 * the viewer has cached can now be wrong: the group's own page
 * (`scope: 'group:<id>'`), the ambient home feed and kind:* feeds
 * (`feedVisible` = member-of-a-readable-scope), and — because child-circle
 * membership cascades to the parent network — that network's page too. We
 * invalidate the broad `feedKeys.lists()` prefix (partial-matches every scoped
 * list regardless of pageSize) plus the sidebar scope counts, mirroring how
 * `useCreateFeedPost` refreshes the feed when a post appears. Only the mounted
 * feed refetches immediately; the rest are marked stale for their next mount.
 */
export function invalidateViewerFeed(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: feedKeys.lists() });
  qc.invalidateQueries({ queryKey: feedKeys.scopeCounts().queryKey });
}

/**
 * Attaching / detaching a circle from a network mutates BOTH entities, so the
 * single-group `invalidateGroupAfterMutation` (keyed by one groupId) can't
 * express it. Covers all three ways the link changes — link, unlink, and
 * creating a circle already linked to a parent network. Refresh:
 *   - the network's child-circle list (admin panel + public detail roster),
 *     refetched even while inactive, and its detail (circle count)
 *   - the circle's detail (its parent network changed) + the circle lists
 *   - the viewer's own `circle.mine()` — REFETCHED, not just invalidated,
 *     because the /circles list is inactive when the link is made from the
 *     network admin panel and would otherwise stay stale until a reload.
 */
export function invalidateAfterCircleNetworkLink(
  qc: QueryClient,
  networkId: string,
  circleId: string,
): void {
  // `circlesAll` is the inner node the leaf composes, so this reaches every
  // arg variant (admin @50 includeArchived, public roster @24) without
  // respelling the key. REFETCHED, not just invalidated: on the create path the
  // circle is created from /circles/new and the admin list is unmounted, so a
  // default invalidate would only mark it stale and the screen it navigates to
  // would render the pre-create pages until the mount refetch settled.
  invalidateAndRefetchAll(qc, {
    queryKey: groupKeys.network.circlesAll(networkId),
  });
  invalidateDetailForKind(qc, 'network', networkId);
  invalidateDetailForKind(qc, 'circle', circleId);
  invalidateListsForKind(qc, 'circle');
  invalidateMineForKind(qc, 'circle');
}

// The member-list key carries a trailing `{ search }` object so each filter
// caches separately. Invalidate by the opts-less prefix so a list that's
// currently filtered by a search term still refetches — an exact
// `members(id)` key builds `{ search: '' }` and would only match the
// unfiltered query, leaving a filtered roster stale until reload.
function invalidateMembersList(qc: QueryClient, groupId: string): void {
  qc.invalidateQueries({ queryKey: [...groupKeys.all, 'members', groupId] });
  // The leader-only roster is a separate leaf feeding the same admin table —
  // without this a leader who promotes or removes someone sees a stale row.
  qc.invalidateQueries({ queryKey: groupKeys.roster(groupId).queryKey });
}

function invalidateDetailForKind(
  qc: QueryClient,
  kind: GroupKind,
  groupId: string,
): void {
  if (kind === 'circle') {
    qc.invalidateQueries({ queryKey: groupKeys.circle.detail(groupId).queryKey });
    return;
  }
  if (kind === 'network') {
    qc.invalidateQueries({ queryKey: groupKeys.network.detail(groupId).queryKey });
    return;
  }
  qc.invalidateQueries({ queryKey: spacesKeys.detail(groupId).queryKey });
}

function invalidateListsForKind(qc: QueryClient, kind: GroupKind): void {
  if (kind === 'circle') {
    qc.invalidateQueries({ queryKey: groupKeys.circle.lists() });
    return;
  }
  if (kind === 'network') {
    qc.invalidateQueries({ queryKey: groupKeys.network.lists() });
    return;
  }
  qc.invalidateQueries({ queryKey: spacesKeys.lists() });
}

function invalidateMineForKind(qc: QueryClient, kind: GroupKind): void {
  if (kind === 'circle') {
    // Opts-less prefix: `circle.mine` is keyed by { includeArchived, pageSize },
    // so refetch every variant (page-6 preview, page-100 picker).
    invalidateAndRefetchAll(qc, {
      queryKey: [...groupKeys.circle.all, 'mine'],
    });
    return;
  }
  if (kind === 'network') {
    // Opts-less prefix: `network.mine` is keyed by { includeArchived, pageSize },
    // so refetch every variant (audience picker @100, browse @default).
    invalidateAndRefetchAll(qc, { queryKey: [...groupKeys.network.all, 'mine'] });
    return;
  }
  // Spaces don't surface a separate "mine" key; the lists() view holds it.
  qc.invalidateQueries({ queryKey: spacesKeys.lists() });
}

function joinRequestsForKind(
  kind: GroupKind,
  groupId: string,
): readonly unknown[] {
  if (kind === 'circle') return groupKeys.circle.joinRequests(groupId).queryKey;
  if (kind === 'network') return groupKeys.network.joinRequests(groupId).queryKey;
  // Spaces don't support request-to-join. Falls through to a no-op key.
  return [...spacesKeys.detail(groupId).queryKey, 'join-requests'] as const;
}
